import React, { useRef } from 'react';
import { Form, useClassNameMapper } from 'react-bootstrap';
import type { FieldValues, Message } from 'react-hook-form';
import type { FieldError } from 'react-hook-form/dist/types/errors';
import type { FieldPath } from 'react-hook-form/dist/types/path';
import { useUIDSeed } from 'react-uid';
import { SharedComponent } from '../../../enums';
import useTranslation from '../../useTranslation';
import type { FormFieldVariant } from '../enums';
import { FormInputFieldViewVariant } from '../enums';
import FormField from '../FormField/FormField';
import FormFieldFeedback from '../FormFieldFeedback/FormFieldFeedback';
import type { AllValidationRules, FormFieldProps, FormFieldType } from '../types';
import { FieldType } from '../types';
import localStyles from './DateInputField.module.pcss';

interface DateValue {
  /**
   * day field in the date
   */
  day: string;
  /**
   * month field in the date
   */
  month: string;
  /**
   * year field in the date
   */
  year: string;
}

/**
 * Input Field definition to render the date input field.
 *
 */
export interface RegistrationDateFieldSpec<
  NameT extends FieldPath<DataT>,
  DataT extends FieldValues
> extends Omit<FormFieldType<NameT, DataT, FormFieldVariant.DATE_INPUT>, 'validations'> {
  /**
   * View variant of the input field.
   */
  viewVariant?: FormInputFieldViewVariant;
}

/**
 * Renders date input field for the form
 *
 * @author Be.Abhijith
 */
const DateInputField = <NameT extends FieldPath<DataT>, DataT extends FieldValues>({
  fieldSpec,
  formId,
  i18n,
  className,
  formMethods,
  submitOnChange,
  onFormSubmit,
  allFields
}: FormFieldProps<NameT, DataT, RegistrationDateFieldSpec<NameT, DataT>>): React.ReactElement => {
  const cx = useClassNameMapper(localStyles);
  const { formatMessage, loading: textLoading } = useTranslation(SharedComponent.DATE_INPUT_FIELD);
  const dateValues = useRef<DateValue>({ day: null, month: null, year: null });
  const { name, viewVariant } = fieldSpec;
  const uidSeed = useUIDSeed();
  const dateItemKey = `${formId}.${name}.day`;
  const monthItemKey = `${formId}.${name}.month`;
  const yearItemKey = `${formId}.${name}.year`;
  const minSelectableYear: number = new Date().getFullYear() - 120;
  const maxSelectableYear: number = new Date().getFullYear();
  const {
    formState: { errors }
  } = formMethods;
  const isErrorSet: boolean = errors[name]?.type == 'manual';

  /**
   * Helper function used to retrieve error messages for the form field.
   *
   * @param fieldErrors - field errors for input fields
   * @returns string - field error message string
   */
  function parseErrorMessage(fieldErrors: FieldError): string {
    const { message } = fieldErrors;
    try {
      return JSON.parse(message);
    } catch {
      return message;
    }
  }

  /**
   * check if any of the fields in date are empty
   *
   * @param date - date string to check if its empty
   * @returns boolean - if date string is empty returns true else false
   */
  function isEmpty(date: string): boolean {
    if (!date) {
      return true;
    }
    const parts: string[] = date.split('/');
    const month: number = +parts[0];
    const day: number = +parts[1];
    const year: number = +parts[2];
    if (!day || !month || !year) {
      return true;
    }
    return false;
  }

  /**
   * the function gets the number of days in specified month and year
   *
   * @param year - year of the date
   * @param month -  month of the date
   */
  const getDays = (year: number, month: number): number => {
    return new Date(year, month, 0).getDate();
  };

  /**
   *  Function to validate the input date
   *  the function get the days in the user specified month and year and validates if the days are within the days in the month of specified year
   *
   * @param dateString - the date string to validate
   * @returns boolean - if date string is valid then it returns true else false
   */
  function validateDate(dateString: string): boolean {
    const parts: string[] = dateString.split('/');
    const month: number = +parts[0];
    const day: number = +parts[1];
    const year: number = +parts[2];
    if (Number.isNaN(day) || Number.isNaN(month) || Number.isNaN(year)) {
      return false;
    }
    if (month < 1 || month > 12 || year < minSelectableYear || year > maxSelectableYear) {
      return false;
    }
    const daysInMonth: number = getDays(year, month);
    if (day < 1 || day > daysInMonth) {
      return false;
    }
    return true;
  }

  const fieldError: string = errors[name] && parseErrorMessage(errors[name] as FieldError);

  /**
   * validation rules for date input fields
   *
   * @returns AllValidationRules - validation rules for all input fields
   */
  const validationRules: AllValidationRules<DataT, NameT> = {
    validate: (value: string): Message | true => {
      if (isEmpty(value)) {
        return formatMessage('empty');
      }
      if (!validateDate(value)) {
        return formatMessage('invalid');
      }
      return true;
    }
  };

  if (textLoading) {
    return null;
  }

  /**
   * handle key down function to not allow '+','-','.','e' characters in number field
   *
   * @param event - The keyboard event for html input element
   */
  function handleKeydown(event: React.KeyboardEvent<HTMLInputElement>): void {
    if (
      event.code === 'Period' ||
      event.code === 'Minus' ||
      event.code === 'Equal' ||
      event.code === 'KeyE' ||
      event.code === 'NumpadAdd' ||
      event.code === 'NumpadSubtract' ||
      event.code === 'NumpadDecimal'
    ) {
      event.preventDefault();
    }
  }

  /**
   * handle change callback for input fields
   *
   *
   * @param event - the event object
   * @param minValue - min value for input fields
   * @param maxValue - max value for input fields
   * @param maxlength - max length for input fields
   * @param key - key for input fields
   * @param onChange - react-hook-form onChange
   */
  function handleChange(
    event: React.ChangeEvent<HTMLInputElement>,
    minValue: number,
    maxValue: number,
    maxlength: number,
    key: 'day' | 'month' | 'year',
    onChange: (value: string) => void
  ): void {
    if (
      (+event.target.value > maxValue || +event.target.value < minValue) &&
      (key === 'day' || key === 'month')
    ) {
      return;
    }
    if (event.target.value.length > maxlength) {
      event.target.value = event.target.value.slice(0, maxlength);
    }
    const currentValue: string = event.target.value;
    dateValues.current[key] = currentValue;
    onChange(`${dateValues.current.month}/${dateValues.current.day}/${dateValues.current.year}`);
  }

  return (
    <FormField<NameT, DataT, FieldType.CONTROLLED>
      fieldSpec={fieldSpec}
      fieldType={FieldType.CONTROLLED}
      formId={formId}
      i18n={i18n}
      className={cx(
        {
          'lia-g-form-field-no-border': viewVariant === FormInputFieldViewVariant.NO_BORDER
        },
        className
      )}
      formMethods={formMethods}
      submitOnChange={submitOnChange}
      onFormSubmit={onFormSubmit}
      allFormFields={allFields}
      validationOverrides={validationRules}
    >
      {({
        render: {
          field: { onChange }
        }
      }) => {
        return (
          <>
            <Form.Group className={cx('lia-form-row')}>
              <Form.Control
                id={uidSeed(monthItemKey)}
                aria-describedby={name}
                type="number"
                placeholder={formatMessage('DateInputField.month.placeholder')}
                className={cx('lia-form-control')}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  handleChange(event, 0, 12, 2, 'month', onChange);
                }}
                onKeyDown={handleKeydown}
                data-testid={`DateInputField.${formId}.month`}
                value={dateValues.current.month ?? ''}
              />
              <Form.Control
                id={uidSeed(dateItemKey)}
                aria-describedby={name}
                type="number"
                placeholder={formatMessage('DateInputField.day.placeholder')}
                className={cx('lia-form-control')}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  handleChange(event, 0, 31, 2, 'day', onChange);
                }}
                onKeyDown={handleKeydown}
                data-testid={`DateInputField.${formId}.day`}
                value={dateValues.current.day ?? ''}
              />
              <Form.Control
                id={uidSeed(yearItemKey)}
                aria-describedby={name}
                type="number"
                placeholder={formatMessage('DateInputField.year.placeholder')}
                className={cx('lia-form-control')}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  handleChange(event, 0, 0, 4, 'year', onChange);
                }}
                onKeyDown={handleKeydown}
                data-testid={`DateInputField.${formId}.year`}
              />
            </Form.Group>
            {fieldError && typeof fieldError === 'string' && !isErrorSet && (
              <FormFieldFeedback id={name} message={fieldError} valid={false} />
            )}
          </>
        );
      }}
    </FormField>
  );
};
export default DateInputField;
